<template>
  <a-i-wrapper
    class="ai-prompt-wrapper"
    :bounce="isHovering">
    <div class="ai-prompt">
      <magic-wand
        class="ai-icon"
        :class="{ spin: isLoading }" />
      <input
        v-model="prompt"
        type="text"
        class="ai-input"
        name="prompt"
        :placeholder="placeHolder"
        @keyup.enter="onGenerate" />
      <button
        class="ai-btn btn btn-primary btn-sm"
        @mouseenter="isHovering = true"
        @mouseleave="isHovering = false"
        @click="onGenerate">
        <stars style="scale: calc(1.25)" />
        Generate
      </button>
    </div>
  </a-i-wrapper>
</template>

<script setup lang="ts" generic="T extends ApiClass">
  import { capitalize, computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import AIWrapper from '@/common/components/AIWrapper.vue';
  import MagicWand from '@/common/icons/monochrome/MagicWand.vue';
  import Stars from '@/common/icons/monochrome/Stars.vue';
  import AIThread from '@/common/models/ai/AIThread';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { API, ApiClass } from '@/utils/ApiUtils';
  import UserNotifications from '@/utils/UserNotifications';

  interface Props {
    suggestion?: string;
    threadFn: API<AIThread>;
    submitFn: API<T>;
  }
  interface Emits {
    (e: 'onComplete', data: T): void;
  }
  const props = defineProps<Props>();
  const emits = defineEmits<Emits>();
  const i18n = useI18n();

  const isLoading = ref(false);
  const isHovering = ref(false);
  const thread = ref(null);
  const prompt = ref('');

  const placeHolder = computed(() => {
    if (props.suggestion) {
      return capitalize(i18n.t('messages.aiPromptCustom', [i18n.t(props.suggestion)]));
    }
    return capitalize(i18n.t('messages.aiPrompt'));
  });

  async function onGenerate() {
    if (!prompt.value) {
      UserNotifications.warning({
        title: capitalize(i18n.t('messages.invalidInput')),
        message: i18n.t('messages.emptyPrompt'),
      });
      return;
    }
    isLoading.value = true;
    GlobalEventBus.$emit(GlobalEvents.loading);
    try {
      const result = await props.submitFn({
        data: {
          thread_id: thread.value,
          prompt: prompt.value,
        },
      });
      emits('onComplete', result);
    } catch (err) {
      UserNotifications.somethingWentWrong(err, 'messages.failedToGetAudienceRule');
    } finally {
      isLoading.value = false;
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
  }
  onMounted(async () => {
    const result = await props.threadFn({});
    thread.value = result.thread;
  });
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .ai-prompt-wrapper {
    width: 100%;
    max-width: 72rem;
  }

  .ai-prompt {
    display: flex;
    position: relative;
    background-color: var(--defaultButtonColorReverse);
    color: var(--defaultButtonColor);
    fill: var(--defaultButtonColorLight);
    padding-left: 1.1rem;
    align-items: center;

    &:focus-within {
      border-color: #5cb3fd;
    }

    .ai-icon {
      height: 2rem;
      fill: var(--actionsColor);
      stroke: var(--actionsColor);
      stroke-width: 0.2rem;
      transition: all 0.2s ease-in-out;
      margin-right: 0 !important;
      scale: 1;
    }

    .ai-input {
      border: none;
      background: none;
      outline: none;
      height: 3.6rem;
      margin-left: 1.1rem;
      flex-grow: 1;

      &:-webkit-autofill {
        background: none !important;
        // Weird thing to remove the blue background from autocomplete
        -webkit-box-shadow: 0 0 0 1000px white inset;
      }
    }

    .ai-btn {
      margin-left: auto;
      height: 3.6rem;
      border-radius: 0 !important;
    }
  }

  @keyframes spin {
    to {
      --angle: 360deg;
    }
  }

  @property --angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }
</style>
